import { useState, useEffect } from 'react';
import { useAppContext } from '@/App';
import { getMyDashboard, getMyDocuments, getMyDocumentDownload } from '@/lib/api';
import { formatDateValue } from '@/lib/dates';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Video, FileText, Award, Clock, ArrowRight, Megaphone, FileDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY_VALUE = '\u2014';
const INTERNAL_ENROLLMENT_STATUSES = new Set(['???', 'GUA']);
const KNOWN_ENROLLMENT_STATUSES = new Set([
  'Admitted',
  'Active',
  'Withdraw',
  'Waiting SP',
  'Waiting Payment',
  'Waiting Grad',
  'Graduated',
  'AOR',
  'Deferred',
  'OA Declined',
]);

const studentDocTypes = [
  { value: 'offer_letter', label: 'Offer Letter' },
  { value: 'enrolment_contract', label: 'Student Enrolment Contract' },
  { value: 'loa', label: 'Official Letter of Acceptance' },
  { value: 'transcript', label: 'Official Transcript' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'receipt', label: 'Receipt' },
  { value: 'fee_payment_instructions', label: 'Fee Payment Instructions' },
];

function displayValue(value) {
  return value === null || value === undefined || value === '' ? EMPTY_VALUE : value;
}

function dateValue(value) {
  return formatDateValue(value, {
    fallback: EMPTY_VALUE,
    locale: 'en-US',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function enrollmentBadge(status) {
  if (!status || INTERNAL_ENROLLMENT_STATUSES.has(status) || !KNOWN_ENROLLMENT_STATUSES.has(status)) {
    return null;
  }
  if (status === 'Active' || status === 'Graduated') {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  }
  if (status === 'Withdraw' || status === 'OA Declined') {
    return 'border-rose-200 bg-rose-50 text-rose-700';
  }
  return 'border-[#062B1A]/15 bg-[#F7F8FA] text-[#062B1A]/65';
}

function ProgramInfoRow({ label, value, badgeClass }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-[#062B1A]/5 py-2.5 last:border-b-0">
      <span className="text-xs font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">{label}</span>
      {badgeClass ? (
        <Badge variant="outline" className={`max-w-[60%] justify-center whitespace-normal text-center ${badgeClass}`}>
          {value}
        </Badge>
      ) : (
        <span className="max-w-[60%] text-right text-sm font-medium text-[#062B1A]">{displayValue(value)}</span>
      )}
    </div>
  );
}

function MyProgramCard({ student, error }) {
  const status = student?.enrollment_status || '';
  const badgeClass = enrollmentBadge(status);
  const statusValue = badgeClass ? status : EMPTY_VALUE;

  return (
    <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          My Program
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && !student ? (
          <p className="text-sm text-rose-700">Program details could not be loaded.</p>
        ) : (
          <>
            {error && <p className="mb-3 text-sm text-rose-700">{error}</p>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <ProgramInfoRow label="Program" value={student?.program_name} />
              <ProgramInfoRow label="Enrollment Status" value={statusValue} badgeClass={badgeClass} />
              <ProgramInfoRow label="Campus" value={student?.campus} />
              <ProgramInfoRow label="Delivery Method" value={student?.delivery_method} />
              <ProgramInfoRow label="Start Date" value={dateValue(student?.start_date)} />
              <ProgramInfoRow label="Expected End Date" value={dateValue(student?.end_date)} />
              <ProgramInfoRow label="Pathway Option" value={student?.pathway_option} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MyDocumentsCard({ documents, loading, error, downloadingDoc, onDownload }) {
  const visibleDocTypes = studentDocTypes.filter((docType) => !documents?.[docType.value]?.hidden);

  return (
    <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          My Documents
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && <p className="mb-3 text-sm text-rose-700">{error}</p>}
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-[#062B1A]/55">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading documents...
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {visibleDocTypes.map((docType) => {
              const doc = documents?.[docType.value] || { available: false };
              const available = Boolean(doc.available);
              return (
                <div key={docType.value} className="rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-4">
                  <div className="mb-3 min-h-[52px]">
                    <p className="text-sm font-semibold text-[#062B1A]">{docType.label}</p>
                    <p className="mt-1 text-xs text-[#062B1A]/50">
                      {available ? `Issued ${dateValue(doc.issued_at)}` : 'Not yet issued'}
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="w-full bg-[#062B1A] text-white hover:bg-[#041B10]"
                    disabled={!available || downloadingDoc === docType.value}
                    onClick={() => onDownload(docType.value)}
                  >
                    {downloadingDoc === docType.value ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileDown className="w-4 h-4 mr-2" />
                    )}
                    Download
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function StudentDashboard() {
  const { currentStudent } = useAppContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState('');
  const [documents, setDocuments] = useState({});
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [documentsError, setDocumentsError] = useState('');
  const [downloadingDoc, setDownloadingDoc] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    const loadDashboard = async () => {
      try {
        setLoading(true);
        setDashboardError('');
        const res = await getMyDashboard();
        if (!cancelled) {
          setData(res.data);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setDashboardError('Program details could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDashboard();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadDocuments = async () => {
      try {
        setDocumentsLoading(true);
        setDocumentsError('');
        const res = await getMyDocuments();
        if (!cancelled) {
          setDocuments(res.data || {});
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setDocumentsError('Documents could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setDocumentsLoading(false);
        }
      }
    };

    loadDocuments();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="h-28 bg-white rounded-xl animate-pulse" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 h-64 bg-white rounded-xl animate-pulse" />
          <div className="h-64 bg-white rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  // Unprovisioned student: /me/dashboard returns {student: null, ...empty arrays}.
  // Show a clear message instead of a row of zero-value KPI tiles.
  if (data && !data.student && (!data.enrolled_courses || data.enrolled_courses.length === 0)) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 text-[#062B1A]/15 mx-auto mb-3" />
          <h2 className="text-lg font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Profile not provisioned yet
          </h2>
          <p className="text-sm text-[#062B1A]/50 mt-2 max-w-md mx-auto">
            Your account is signed in, but a student record hasn't been linked yet. Contact your administrator if this persists.
          </p>
        </div>
      </div>
    );
  }

  const displayName = (data?.student?.name || currentStudent?.name || '').split(' ')[0];
  const programStudent = data?.student || currentStudent || null;

  const handleDocumentDownload = async (docType) => {
    try {
      setDownloadingDoc(docType);
      const res = await getMyDocumentDownload(docType);
      if (res.data?.url) {
        window.open(res.data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error(err);
      toast.error('Download link could not be created.');
    } finally {
      setDownloadingDoc('');
    }
  };

  const kpiData = [
    { label: 'Enrolled Courses', value: data?.enrolled_courses?.length || 0, icon: BookOpen, color: 'text-[#062B1A]', accent: false },
    { label: 'Upcoming Sessions', value: data?.upcoming_sessions?.length || 0, icon: Video, color: 'text-[#D4AF37]', accent: true },
    { label: 'Pending Assignments', value: data?.pending_assignments?.length || 0, icon: FileText, color: 'text-[#062B1A]', accent: false },
    { label: 'Upcoming Exams', value: data?.upcoming_exams?.length || 0, icon: Award, color: 'text-[#D4AF37]', accent: true },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome */}
      <div>
        <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
          {displayName ? `Welcome back, ${displayName}` : 'Welcome back'}
        </h2>
        <p className="text-sm text-[#062B1A]/60 mt-1">Here's an overview of your learning progress</p>
      </div>

      <MyProgramCard student={programStudent} error={dashboardError} />

      <MyDocumentsCard
        documents={documents}
        loading={documentsLoading}
        error={documentsError}
        downloadingDoc={downloadingDoc}
        onDownload={handleDocumentDownload}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="student-dashboard-kpis">
        {kpiData.map((kpi, idx) => (
          <Card key={idx} className={`card-hover border border-[rgba(6, 43, 26,0.08)] shadow-sm ${kpi.accent ? 'gold-border-left' : ''}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">{kpi.label}</span>
                <kpi.icon className={`w-4 h-4 ${kpi.color}`} />
              </div>
              <p className="text-3xl font-bold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>{kpi.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Continue Learning + Upcoming Sessions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Continue Learning */}
        <Card className="lg:col-span-2 border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Continue Learning
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data?.enrolled_courses?.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="w-10 h-10 text-[#062B1A]/20 mx-auto mb-2" />
                <p className="text-sm text-[#062B1A]/50">No courses registered yet</p>
                {/* Registration is registrar-controlled, so pointing a student
                    at the catalog to "enroll" would be a dead end. */}
                <p className="mx-auto mt-1 max-w-xs text-xs text-[#062B1A]/40">
                  Your courses appear here once the registrar has registered you.
                </p>
                <Button variant="outline" className="mt-3" size="sm" onClick={() => navigate('/student/catalog')}>Browse Catalog</Button>
              </div>
            ) : (
              data?.enrolled_courses?.map((course, idx) => {
                const progress = data?.progress?.[course.id] || 0;
                return (
                  <div key={course.id} className="flex items-center gap-4 p-3 rounded-lg bg-[#F7F8FA] hover:bg-[rgba(212, 175, 55,0.06)] transition-colors cursor-pointer" onClick={() => navigate(`/student/courses/${course.id}`)}>
                    <div className="w-10 h-10 rounded-lg bg-[#062B1A] flex items-center justify-center flex-shrink-0">
                      <BookOpen className="w-5 h-5 text-[#D4AF37]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#062B1A] truncate">{course.title}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <Progress value={progress} className="h-1.5 flex-1 bg-[#062B1A]/10 [&>div]:bg-[#D4AF37]" />
                        <span className="text-xs text-[#062B1A]/50 flex-shrink-0">{progress}%</span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#062B1A]/30 flex-shrink-0" />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Upcoming Sessions */}
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Upcoming Sessions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data?.upcoming_sessions?.length === 0 ? (
              <p className="text-sm text-[#062B1A]/50 text-center py-6">No upcoming sessions</p>
            ) : (
              data?.upcoming_sessions?.map((session) => (
                <div key={session.id} className="p-3 rounded-lg border border-[rgba(6, 43, 26,0.08)] hover:border-[#D4AF37]/30 transition-colors">
                  <p className="text-sm font-medium text-[#062B1A]">{session.title}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <Clock className="w-3 h-3 text-[#D4AF37]" />
                    <span className="text-xs text-[#062B1A]/50">
                      {new Date(session.scheduled_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {session.meeting_link && (
                    <Button size="sm" className="mt-2 w-full h-7 text-xs bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={() => window.open(session.meeting_link, '_blank')}>Join Session</Button>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Grades + Announcements */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Grades */}
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Recent Grades</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.recent_grades?.length === 0 ? (
              <p className="text-sm text-[#062B1A]/50 text-center py-6">No grades yet</p>
            ) : (
              <div className="space-y-2">
                {data?.recent_grades?.map((sub) => (
                  <div key={sub.id} className="flex items-center justify-between p-3 rounded-lg bg-[#F7F8FA]">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs border-[#062B1A]/20">{sub.type}</Badge>
                      <span className="text-sm text-[#062B1A]">{sub.score}/{sub.max_score}</span>
                    </div>
                    <span className="text-xs text-[#062B1A]/50">{sub.status}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Announcements */}
        <Card className="border border-[rgba(6, 43, 26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>Announcements</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.announcements?.length === 0 ? (
              <p className="text-sm text-[#062B1A]/50 text-center py-6">No announcements</p>
            ) : (
              <div className="space-y-3">
                {data?.announcements?.map((ann) => (
                  <div key={ann.id} className="p-3 rounded-lg border border-[rgba(6, 43, 26,0.08)]">
                    <div className="flex items-center gap-2 mb-1">
                      <Megaphone className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <p className="text-sm font-medium text-[#062B1A]">{ann.title}</p>
                    </div>
                    <p className="text-xs text-[#062B1A]/60 line-clamp-2">{ann.content}</p>
                    <p className="text-[10px] text-[#062B1A]/40 mt-1">{ann.author} • {new Date(ann.created_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
